import type { UserId } from '@hprofits/shared';
import { MaybeRefOrGetter, toValue } from 'vue';
import { computed } from 'vue';

import type { LegacyAuthApi } from '@/composables/api/useLegacyCreativeApi';
import { useCurrentUserId } from '@/composables/useCurrentUserId';
import { useStore } from '@/composables/useVuexStore';

import { useLegacyOwnerGrantsQuery } from './useLegacyOwnerGrantsQuery';

type UseOwnerGrantsOptions = {
  isAdmin: MaybeRefOrGetter<boolean>;
  campaignGroupOwnerId: MaybeRefOrGetter<UserId | null>;
  authApi?: LegacyAuthApi;
};

type Grant = {
  id: number;
  actionName: string;
};

type UserGrant = {
  actionName: string;
};

export const useOwnerGrants = (options: UseOwnerGrantsOptions) => {
  const store = useStore();
  const currentUserId = useCurrentUserId();

  // TODO(#520): Replace with dedicated backend endpoint (e.g. GET /users/:id/grants).
  // HACK: Disguise as campaign owner → GET /profile → extract grant IDs to check capabilities.
  // Read-only, no side effects.

  const needsOwnerGrants = computed(() => {
    const ownerId = toValue(options.campaignGroupOwnerId);
    return toValue(options.isAdmin) && ownerId != null && ownerId !== toValue(currentUserId);
  });

  const ownerGrantsQuery = useLegacyOwnerGrantsQuery({
    authApi: options.authApi,
    ownerId: options.campaignGroupOwnerId,
    enabled: needsOwnerGrants,
  });

  // null when needsOwnerGrants is false (non-admin or self) or query is pending/error
  // (hides actions until resolved, mirroring old catch→null semantics).
  const ownerGrantActions = computed<Set<string> | null>(() => {
    if (!needsOwnerGrants.value) {
      return null;
    }
    const grantIds = ownerGrantsQuery.data.value;
    if (grantIds === undefined) {
      return null;
    }
    // TODO(L1-1): migrate store.getters.grants/userGrants off Vuex; also consumed by plugins/index.js (CASL).
    const allGrants: Array<Grant> = store.getters.grants ?? [];
    return new Set(allGrants.filter(({ id }) => grantIds.has(id)).map(({ actionName }) => actionName));
  });

  const hasEffectiveGrant = (actionName: string): boolean => {
    if (needsOwnerGrants.value) {
      if (ownerGrantActions.value === null) {
        return false;
      }
      return ownerGrantActions.value.has(actionName);
    }
    // TODO(L1-1): migrate store.getters.grants/userGrants off Vuex; also consumed by plugins/index.js (CASL).
    const grants: Array<UserGrant> = store.getters.userGrants ?? [];
    return grants.some((g) => g.actionName === actionName);
  };

  return { hasEffectiveGrant, ownerGrantActions };
};
